import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import Email from "app/pages/.shared/profile/Email";
import Separator from "app/pages/.shared/Separator";
import "./PaymentContact.scss";
import LoadingPlaceholder from "app/pages/.shared/LoadingPlaceholder";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import useOnScreen from "app/pages/.shared/useOnScreen";
import IdentityFieldset from "app/pages/.shared/profile/IdentityFieldset";
import AddressFieldset from "app/pages/.shared/profile/AddressFieldset";
import { useFormikContext } from "formik";
import PhoneIntl from "app/pages/.shared/profile/PhoneIntl";
import FormErrorMessages from "app/pages/.shared/form/FormErrorMessages/FormErrorMessages";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import Popover from "app/components/ui/Popover/Popover";
import { ButtonLink } from "app/components/ui/ButtonLink/ButtonLink";

const PaymentContact = ({
	isLoading,
	shop,
	shouldDisableEmailField,
	confidentialiteDocumentName,
}) => {
	const {
		setFieldValue,
		setFieldTouched,
		errors = {},
		setFieldError,
		isSubmitting,
		submitCount,
	} = useFormikContext();
	const { track } = useAnalytics();
	const ref = useRef();
	const isElementOnScreen = useOnScreen({ ref, triggerOnce: false });

	useEffect(() => {
		if (isElementOnScreen && !isLoading) {
			track("payment_view_contact");
		}
	}, [isElementOnScreen, isLoading]);

	return (
		<div className="payment-contact" ref={ref}>
			{isLoading ? (
				<LoadingPlaceholder
					message={<FormattedMessage id="payment.contact.loading.text" />}
				/>
			) : (
				<>
					<FormErrorMessages
						errors={errors}
						fieldsFilter={["contact"]}
						isSubmitting={isSubmitting}
						submitCount={submitCount}
						message={<FormattedMessage id="error.booking.payment.fields.required" />}
					/>

					<Typography
						variant={TYPOGRAPHY_VARIANTS.LARGE}
						isBold
						className="payment__subtitle"
						component="h3"
					>
						<FormattedMessage id="payment.contact.contact" />
					</Typography>

					<IdentityFieldset isRequired={true} blocPrefixName="contact" />

					<div className="payment__email-phone">
						{shouldDisableEmailField ? (
							<Email disabled blocPrefixName="contact" />
						) : (
							<Email blocPrefixName="contact" />
						)}
						<PhoneIntl
							id="phone"
							name="contact.phone"
							shop={shop}
							setFieldValue={setFieldValue}
							setFieldTouched={setFieldTouched}
							setFieldError={setFieldError}
							label={<FormattedMessage id="phone.label" />}
							data-testid="phone-input"
							isRequired={true}
						/>
					</div>

					<div className="payment-contact__data-notice">
						<Typography variant={TYPOGRAPHY_VARIANTS.SMALL} component="span">
							<FormattedMessage id="payment.contact.data.text" />
						</Typography>{" "}
						<Popover iconClassName="payment-contact__info-icon">
							<div className="payment-contact__popover-content">
								<FormattedMessage
									id="payment.contact.data.info.text"
									values={{
										link: confidentialiteDocumentName ? (
											<ButtonLink
												variant="borderless"
												to={confidentialiteDocumentName}
												target="_blank"
												rel="noopener noreferrer"
												className="payment-contact__link"
											>
												<FormattedMessage id="menu.item.confidentialite" />
											</ButtonLink>
										) : (
											<FormattedMessage id="menu.item.confidentialite" />
										),
									}}
								/>
							</div>
						</Popover>
					</div>

					<Separator design="middle" />

					<Typography
						variant={TYPOGRAPHY_VARIANTS.LARGE}
						isBold
						className="payment__subtitle"
						component="h3"
					>
						<FormattedMessage id="payment.contact.address" />
					</Typography>

					<AddressFieldset
						isRequired
						setFieldValue={setFieldValue}
						blocPrefixName="contact"
						setFieldError={setFieldError}
					/>
				</>
			)}
		</div>
	);
};

PaymentContact.propTypes = {
	shop: PropTypes.string,
	shouldDisableEmailField: PropTypes.bool,
	isLoading: PropTypes.bool,
	confidentialiteDocumentName: PropTypes.string,
};

export default PaymentContact;
